import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api, { waAPI, clientsAPI } from '../../services/api';
import { formatDateTimeMY } from '../../utils/dateUtils';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  ArrowLeft,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  SkipForward,
  RefreshCw,
  Loader2,
  Play,
  Pause,
  ExternalLink,
  Download,
  Eye,
  RotateCcw,
  MessageSquare,
  X,
  Image,
  Copy,
  Check,
  UserPlus,
  UsersRound,
  Search,
  Users
} from 'lucide-react';

const WASendQueuePage = () => {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [copiedImageId, setCopiedImageId] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Preview modal
  const [showPreview, setShowPreview] = useState(false);
  const [previewMessage, setPreviewMessage] = useState(null);
  
  // Batch edit
  const [showBatchEdit, setShowBatchEdit] = useState(false);
  const [editSlots, setEditSlots] = useState([]);
  const [batchSaving, setBatchSaving] = useState(false);
  
  // Batch tabs
  const [batches, setBatches] = useState([]);
  const [selectedBatch, setSelectedBatch] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [batchInitDone, setBatchInitDone] = useState(false);
  
  // Image preview modal
  const [imagePreviewUrl, setImagePreviewUrl] = useState(null);
  
  // Action loading states
  const [actionLoading, setActionLoading] = useState({});
  
  // Recipients for scheduled/draft campaigns
  const [recipients, setRecipients] = useState([]);
  const [recipientsSource, setRecipientsSource] = useState('');
  
  // Add clients modal
  const [showAddClientsModal, setShowAddClientsModal] = useState(false);
  const [availableClients, setAvailableClients] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedClients, setSelectedClients] = useState([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [addingClients, setAddingClients] = useState(false);
  
  // Add group modal
  const [showAddGroupModal, setShowAddGroupModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [addingGroup, setAddingGroup] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const currentFilter = filter;
      const queueParams = { 
        status: currentFilter !== 'all' ? currentFilter : undefined,
        page,
        limit: 10
      };
      // For batch campaigns, filter by selected batch
      if (selectedBatch !== null && selectedBatch !== 'pending') {
        queueParams.batch_number = selectedBatch;
      } else if (selectedBatch === 'pending') {
        // Show all pending messages across all batches
        queueParams.status = 'pending';
      }

      // When viewing "Pending" step, override filter display
      const effectiveFilter = selectedBatch === 'pending' ? 'pending' : currentFilter;
      const [campaignRes, queueRes, recipientsRes] = await Promise.all([
        api.get(`/wa/campaigns/${campaignId}`),
        api.get(`/wa/campaigns/${campaignId}/queue`, { params: queueParams }),
        waAPI.getCampaignRecipients(campaignId)
      ]);
      // Only update state if the filter hasn't changed during the request
      if (currentFilter === filter) {
        setCampaign(campaignRes.data);
        setQueue(queueRes.data.items);
        setTotalPages(queueRes.data.total_pages);
        setTotal(queueRes.data.total);
        setRecipients(recipientsRes.data.recipients || []);
        setRecipientsSource(recipientsRes.data.source || '');
        // Init batch edit slots
        if (campaignRes.data.batch_mode && campaignRes.data.batch_time_slots && editSlots.length === 0) {
          setEditSlots(campaignRes.data.batch_time_slots);
        }
        // Fetch batches for batch campaigns
        if (campaignRes.data.batch_mode) {
          try {
            const batchRes = await api.get(`/wa/campaigns/${campaignId}/batches`);
            setBatches(batchRes.data.batches || []);
            // Auto-select only on first load
            if (!batchInitDone) {
              setBatchInitDone(true);
              const bList = batchRes.data.batches || [];
              const pendingCount = campaignRes.data.stats?.pending || 0;
              const allBatchesDone = bList.every(b => b.status === 'completed' || b.status === 'sent');
              
              if (allBatchesDone && pendingCount > 0) {
                setSelectedBatch('pending');
                setFilter('pending');
              } else {
                const firstActive = bList.find(b => 
                  b.status === 'current' || b.status === 'upcoming' || b.status === 'paused' ||
                  (b.stats.total > 0 && b.stats.sent + b.stats.skipped + b.stats.failed < b.stats.total)
                );
                setSelectedBatch(firstActive ? firstActive.batch_number : 0);
              }
            }
          } catch {}
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [campaignId, filter, page, selectedBatch]);

  // Keep a ref to the latest fetchData for auto-refresh
  const fetchDataRef = useRef(fetchData);
  useEffect(() => { fetchDataRef.current = fetchData; }, [fetchData]);

  useEffect(() => {
    fetchData();
    
    // Auto-refresh every 10 seconds when campaign is running
    const interval = setInterval(() => {
      if (campaign?.status === 'running') {
        fetchDataRef.current();
      }
    }, 10000);
    
    return () => clearInterval(interval);
  }, [fetchData]);

  const handleStartCampaign = async () => {
    setStarting(true);
    try {
      await api.post(`/wa/campaigns/${campaignId}/start`);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error starting campaign');
    } finally {
      setStarting(false);
    }
  };

  const [forceActivating, setForceActivating] = useState(false);
  const [debugInfo, setDebugInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);

  const handleForceActivateBatch = async () => {
    if (!window.confirm('Force activate the current batch? This bypasses all time/day checks.')) return;
    setForceActivating(true);
    try {
      const res = await api.post(`/wa/campaigns/${campaignId}/force-activate-batch`);
      if (res.data.success) {
        alert(`Batch ${res.data.activated_batch + 1} activated (${res.data.activated_items} items). Status: ${res.data.new_status}`);
      } else if (res.data.error) {
        alert(`Error: ${res.data.error}`);
      }
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error force-activating batch');
    } finally {
      setForceActivating(false);
    }
  };

  const handleDebugScheduler = async () => {
    try {
      const res = await api.get(`/wa/campaigns/${campaignId}/debug-scheduler`);
      setDebugInfo(res.data);
      setShowDebug(true);
    } catch (error) {
      alert(error.response?.data?.detail || 'Error fetching debug info');
    }
  };

  const saveBatchSlots = async () => {
    setBatchSaving(true);
    try {
      await api.put(`/wa/campaigns/${campaignId}/batch-settings`, { batch_time_slots: editSlots });
      setShowBatchEdit(false);
      fetchData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save');
    } finally {
      setBatchSaving(false);
    }
  };

  const doExport = async (batchNum) => {
    try {
      const params = {};
      if (batchNum !== null) params.batch_number = batchNum;
      const res = await api.get(`/wa/campaigns/${campaignId}/export`, { params, responseType: 'blob' });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^"]+)"?/);
      const filename = match ? match[1] : 'CRM_Export.xlsx';
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch {}
  };

  const handlePauseCampaign = async () => {
    try {
      await api.post(`/wa/campaigns/${campaignId}/pause`);
      fetchData();
    } catch (error) {
    }
  };

  const handleResumeCampaign = async () => {
    try {
      await api.post(`/wa/campaigns/${campaignId}/resume`);
      fetchData();
    } catch (error) {
    }
  };

  // Add Clients Modal Functions
  const openAddClientsModal = async () => {
    setShowAddClientsModal(true);
    setLoadingClients(true);
    setSelectedClients([]);
    setSearchTerm('');
    
    try {
      const res = await clientsAPI.getAll({ limit: 100 });
      setAvailableClients(res.data.clients || []);
    } catch (error) {
    } finally {
      setLoadingClients(false);
    }
  };

  const searchClients = async () => {
    if (!searchTerm.trim()) return;
    
    setLoadingClients(true);
    try {
      const res = await clientsAPI.getAll({ search: searchTerm, limit: 100 });
      setAvailableClients(res.data.clients || []);
    } catch (error) {
    } finally {
      setLoadingClients(false);
    }
  };

  const handleAddClients = async () => {
    if (selectedClients.length === 0) return;
    
    setAddingClients(true);
    try {
      const response = await waAPI.addClientsToCampaign(campaignId, selectedClients);
      alert(response.data.message || `Added ${response.data.added} clients`);
      setShowAddClientsModal(false);
      fetchData();
    } catch (error) {
      alert('Failed to add clients');
    } finally {
      setAddingClients(false);
    }
  };

  // Add Group Modal Functions
  const openAddGroupModal = async () => {
    setShowAddGroupModal(true);
    setLoadingGroups(true);
    setSelectedGroup(null);
    
    try {
      const res = await waAPI.getGroups();
      setGroups(res.data || []);
    } catch (error) {
    } finally {
      setLoadingGroups(false);
    }
  };

  const handleAddGroup = async () => {
    if (!selectedGroup) return;
    
    setAddingGroup(true);
    try {
      const response = await waAPI.addGroupToCampaign(campaignId, selectedGroup);
      alert(response.data.message || `Added clients from group`);
      setShowAddGroupModal(false);
      fetchData();
    } catch (error) {
      alert('Failed to add group');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleOpenWhatsApp = (message) => {
    // Open WhatsApp in new tab
    window.open(message.wa_link, '_blank');
  };

  const handleMarkSent = async (messageId) => {
    setActionLoading({ ...actionLoading, [messageId]: 'sent' });
    try {
      await api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/sent`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading({ ...actionLoading, [messageId]: null });
    }
  };

  // Reason modal
  const [reasonModal, setReasonModal] = useState(null); // {messageId, action: 'skip'|'failed'}
  const [reasonText, setReasonText] = useState('');

  const SKIP_REASONS = [
    "Phone number doesn't exist on WhatsApp",
    "Invalid phone number format",
    "Customer blocked/unavailable",
    "WhatsApp disconnected",
  ];

  const handleMarkFailed = async (messageId, reason = '') => {
    setActionLoading({ ...actionLoading, [messageId]: 'failed' });
    try {
      await api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/failed`, { reason });
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading({ ...actionLoading, [messageId]: null });
    }
  };

  const handleSkip = async (messageId, reason = '') => {
    setActionLoading({ ...actionLoading, [messageId]: 'skip' });
    try {
      await api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/skip`, { reason });
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading({ ...actionLoading, [messageId]: null });
    }
  };

  const submitReasonAndAction = () => {
    if (!reasonModal) return;
    const { messageId, action } = reasonModal;
    if (action === 'skip') handleSkip(messageId, reasonText);
    else handleMarkFailed(messageId, reasonText);
    setReasonModal(null);
    setReasonText('');
  };

  const handleRetry = async (messageId) => {
    setActionLoading({ ...actionLoading, [messageId]: 'retry' });
    try {
      await api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/retry`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading({ ...actionLoading, [messageId]: null });
    }
  };

  const handleReset = async (messageId) => {
    setActionLoading({ ...actionLoading, [messageId]: 'reset' });
    try {
      await api.post(`/wa/campaigns/${campaignId}/queue/${messageId}/retry`);
      fetchData();
    } catch (error) {
    } finally {
      setActionLoading({ ...actionLoading, [messageId]: null });
    }
  };

  const openPreview = (message) => {
    setPreviewMessage(message);
    setShowPreview(true);
  };

  const handleCopyImage = async (imageUrl, messageId) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      // Convert to PNG for clipboard compatibility
      const img = document.createElement('img');
      img.src = URL.createObjectURL(blob);
      await new Promise(resolve => { img.onload = resolve; });
      const canvas = document.createElement('canvas');
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      canvas.getContext('2d').drawImage(img, 0, 0);
      const pngBlob = await new Promise(resolve => canvas.toBlob(resolve, 'image/png'));
      URL.revokeObjectURL(img.src);
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': pngBlob })]);
      setCopiedImageId(messageId);
      setTimeout(() => setCopiedImageId(null), 2000);
    } catch (e) {
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-yellow-100 text-yellow-700', icon: <Clock size={14} /> },
      sent: { color: 'bg-green-100 text-green-700', icon: <CheckCircle size={14} /> },
      failed: { color: 'bg-red-100 text-red-700', icon: <XCircle size={14} /> },
      skipped: { color: 'bg-gray-100 text-gray-700', icon: <SkipForward size={14} /> }
    };
    return badges[status] || badges.pending;
  };

  const getProgressPercentage = () => {
    if (!campaign?.stats?.total) return 0;
    return Math.round((campaign.stats.sent / campaign.stats.total) * 100);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => navigate('/whatsapp/campaigns')}
        >
          <ArrowLeft size={16} className="mr-2" />
          Back
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">{campaign?.name}</h1>
          <p className="text-gray-500">
            {campaign?.status === 'draft' 
              ? 'Click Start to generate the send queue' 
              : 'Send messages to your clients'}
          </p>
        </div>
        
        {campaign?.status === 'draft' && (
          <Button 
            onClick={handleStartCampaign}
            disabled={starting}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
          >
            {starting ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Play size={16} className="mr-2" />
            )}
            Start Campaign
          </Button>
        )}
        
        {(campaign?.status === 'running' || campaign?.status === 'scheduled') && (
          <Button 
            onClick={handlePauseCampaign}
            variant="outline"
            className="border-yellow-300 text-yellow-700 hover:bg-yellow-50"
          >
            <Pause size={16} className="mr-2" />
            Pause
          </Button>
        )}
        
        {campaign?.status === 'paused' && (
          <Button 
            onClick={handleResumeCampaign}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
          >
            <Play size={16} className="mr-2" />
            Resume
          </Button>
        )}

        {/* Force Activate — for stuck scheduled batch campaigns */}
        {campaign?.status === 'scheduled' && campaign?.batch_mode && (
          <>
            <Button 
              onClick={handleForceActivateBatch}
              disabled={forceActivating}
              className="bg-orange-500 hover:bg-orange-600 text-white"
              data-testid="force-activate-batch-btn"
            >
              {forceActivating ? (
                <Loader2 className="animate-spin mr-2" size={16} />
              ) : (
                <Play size={16} className="mr-2" />
              )}
              Force Activate Batch
            </Button>
            <Button 
              onClick={handleDebugScheduler}
              variant="outline"
              className="border-gray-300 text-gray-600"
              data-testid="debug-scheduler-btn"
            >
              <Eye size={16} className="mr-2" />
              Debug
            </Button>
          </>
        )}
        
        {/* Add Client/Group Buttons - show for running/scheduled/draft campaigns */}
        {['running', 'scheduled', 'draft', 'paused'].includes(campaign?.status) && (
          <>
            <Button 
              variant="outline"
              onClick={openAddGroupModal}
              className="border-purple-200 text-purple-600 hover:bg-purple-50"
            >
              <UsersRound size={16} className="mr-2" />
              Add Client Group
            </Button>
            <Button 
              onClick={openAddClientsModal}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
            >
              <UserPlus size={16} className="mr-2" />
              Add Clients
            </Button>
          </>
        )}
      </div>

      {/* Draft Campaign - Show Start Instructions */}
      {campaign?.status === 'draft' && (
        <Card className="p-8 text-center bg-white border border-gray-100">
          <Play className="mx-auto text-gray-300 mb-4" size={48} />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Campaign Ready to Start</h3>
          <p className="text-gray-500 mb-6 max-w-md mx-auto">
            Click "Start Campaign" to generate the send queue. Messages will be created for all members in the selected group.
          </p>
          <Button 
            onClick={handleStartCampaign}
            disabled={starting}
            className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
          >
            {starting ? (
              <Loader2 className="animate-spin mr-2" size={16} />
            ) : (
              <Play size={16} className="mr-2" />
            )}
            Start Campaign
          </Button>
        </Card>
      )}

      {/* Stats Cards */}
      {campaign?.status !== 'draft' && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="p-4 bg-white border border-gray-100">
            <p className="text-sm text-gray-500">Total</p>
            <p className="text-2xl font-bold text-gray-900">{campaign?.stats?.total || 0}</p>
          </Card>
          <Card className="p-4 bg-white border border-gray-100">
            <p className="text-sm text-gray-500">Sent</p>
            <p className="text-2xl font-bold text-green-600">{campaign?.stats?.sent || 0}</p>
          </Card>
          <Card className="p-4 bg-white border border-gray-100">
            <p className="text-sm text-gray-500">Pending</p>
            <p className="text-2xl font-bold text-yellow-600">{campaign?.stats?.pending || 0}</p>
          </Card>
          <Card className="p-4 bg-white border border-gray-100">
            <p className="text-sm text-gray-500">Failed</p>
            <p className="text-2xl font-bold text-red-600">{campaign?.stats?.failed || 0}</p>
          </Card>
          <Card className="p-4 bg-white border border-gray-100">
            <p className="text-sm text-gray-500">Progress</p>
            <div className="flex items-center gap-2">
              <div className="flex-1 bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[#25D366] h-2 rounded-full transition-all" 
                  style={{ width: `${getProgressPercentage()}%` }}
                />
              </div>
              <span className="text-lg font-bold text-gray-900">{getProgressPercentage()}%</span>
            </div>
          </Card>
        </div>
      )}

      {/* Batch Mode Info + Timeline */}
      {campaign?.batch_mode && (
        <Card className="bg-white border border-gray-100" data-testid="batch-info-banner">
          <div className="p-4 flex items-center justify-between border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-600 font-bold text-sm">B</div>
              <div>
                <p className="font-medium text-gray-900">Batch Mode</p>
                <p className="text-xs text-gray-500">
                  {campaign.batch_size || 10} clients per batch • {batches.length} batch(es) • {(campaign.batch_time_slots || []).length} time slot(s)
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {campaign?.batch_mode && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={async () => {
                    try {
                      const res = await api.post(`/wa/campaigns/${campaignId}/dedup-queue`);
                      alert(`Removed ${res.data.removed} duplicate(s). ${res.data.remaining} items remaining.`);
                      fetchData();
                    } catch {}
                  }}
                  className="text-amber-700 border-amber-200 hover:bg-amber-50"
                  data-testid="dedup-btn"
                >
                  Clean Duplicates
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBatchEdit(!showBatchEdit)}
                className="text-indigo-700 border-indigo-200 hover:bg-indigo-100"
                data-testid="edit-batch-btn"
              >
                {showBatchEdit ? 'Close' : 'Edit Time Slots'}
              </Button>
            </div>
          </div>

          {/* Batch Timeline */}
          {batches.length > 0 && (
            <div className="p-4 overflow-x-auto">
              <div className="flex items-center gap-1 min-w-max">
                {batches.map((b, idx) => {
                  const isSelected = selectedBatch === b.batch_number;
                  const slot = b.time_slot;
                  const timeStr = `${String(slot.start_hour).padStart(2,'0')}:${String(slot.start_minute).padStart(2,'0')}`;
                  const bgClass = b.status === 'paused'
                    ? 'bg-red-50 border-red-300 text-red-700'
                    : b.status === 'completed' || b.status === 'sent'
                    ? 'bg-green-100 border-green-300 text-green-800'
                    : b.status === 'current'
                    ? 'bg-yellow-100 border-yellow-300 text-yellow-800'
                    : 'bg-gray-50 border-gray-200 text-gray-500';
                  return (
                    <React.Fragment key={b.batch_number}>
                      <button
                        onClick={() => { setSelectedBatch(b.batch_number); setPage(1); setFilter('all'); }}
                        className={`flex-shrink-0 px-3 py-2 rounded-lg border text-xs transition-all cursor-pointer ${bgClass} ${isSelected ? 'ring-2 ring-[#4F46E5] ring-offset-1' : 'hover:shadow-sm'}`}
                        data-testid={`batch-step-${b.batch_number}`}
                      >
                        <p className="font-semibold">Batch {b.batch_number + 1} {b.is_paused ? '⏸' : ''}</p>
                        <p className="text-[10px] opacity-75">Day {b.day} • {timeStr}</p>
                        <p className="text-[10px]">{b.client_range} ({b.stats.total > 0 ? `${b.stats.sent}/${b.stats.total} sent` : `${b.client_count} clients`})</p>
                      </button>
                      {idx < batches.length - 1 && (
                        <div className={`w-4 h-0.5 flex-shrink-0 ${b.status === 'completed' || b.status === 'sent' ? 'bg-green-300' : 'bg-gray-200'}`} />
                      )}
                    </React.Fragment>
                  );
                })}
                {/* Pending Messages box — appears when there are pending items */}
                {(campaign?.stats?.pending || 0) > 0 && (
                  <>
                    <div className={`w-4 h-0.5 flex-shrink-0 bg-gray-200`} />
                    <button
                      onClick={() => { setSelectedBatch('pending'); setPage(1); setFilter('pending'); }}
                      className={`flex-shrink-0 px-3 py-2 rounded-lg border text-xs transition-all cursor-pointer bg-amber-100 border-amber-300 text-amber-800 ${selectedBatch === 'pending' ? 'ring-2 ring-[#4F46E5] ring-offset-1' : 'hover:shadow-sm animate-pulse'}`}
                      data-testid="batch-step-pending"
                    >
                      <p className="font-semibold">Pending</p>
                      <p className="text-[10px]">{campaign.stats.pending} message(s)</p>
                    </button>
                  </>
                )}
              </div>
              {/* Selected batch info + actions */}
              {selectedBatch === 'pending' && (
                <div className="mt-3 p-3 bg-amber-50 rounded-lg border border-amber-200 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-medium text-amber-800">Pending Messages ({campaign?.stats?.pending || 0})</span>
                    <span className="text-xs text-amber-600">Messages awaiting action across all batches</span>
                  </div>
                  <button
                    onClick={() => { setSelectedBatch(null); setPage(1); }}
                    className="text-xs text-[#4F46E5] hover:underline"
                  >View All</button>
                </div>
              )}
              {selectedBatch !== null && selectedBatch !== 'pending' && (() => {
                const b = batches.find(x => x.batch_number === selectedBatch);
                if (!b) return null;
                const slot = b.time_slot;
                return (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center justify-between flex-wrap gap-3">
                    <div className="flex items-center gap-4">
                      <span className="text-sm font-medium text-gray-700">Batch {selectedBatch + 1}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-gray-500">Time:</span>
                        <input
                          type="time"
                          value={`${String(slot.start_hour).padStart(2,'0')}:${String(slot.start_minute).padStart(2,'0')}`}
                          onChange={(e) => {
                            const [h, m] = e.target.value.split(':').map(Number);
                            setBatchSaving(true);
                            api.put(`/wa/campaigns/${campaignId}/batch-settings`, {
                              batch_override: { batch_number: selectedBatch, start_hour: h, start_minute: m }
                            }).then(() => fetchData()).finally(() => setBatchSaving(false));
                          }}
                          className="border border-gray-200 rounded px-2 py-1 text-xs w-28 bg-white"
                          data-testid="batch-time-override"
                        />
                      </div>
                      <button
                        onClick={() => { setSelectedBatch(null); setPage(1); }}
                        className="text-xs text-[#4F46E5] hover:underline"
                      >View All</button>
                    </div>
                    <div className="flex items-center gap-2">
                      {b.is_paused ? (
                        <Button size="sm" variant="outline" className="text-green-600 border-green-200 hover:bg-green-50 text-xs h-7"
                          onClick={() => {
                            api.put(`/wa/campaigns/${campaignId}/batch-settings`, { resume_batch: selectedBatch }).then(() => fetchData());
                          }} data-testid="resume-batch-btn">
                          Resume Batch
                        </Button>
                      ) : (b.status === 'upcoming' || b.status === 'current') && (
                        <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50 text-xs h-7"
                          onClick={() => {
                            api.put(`/wa/campaigns/${campaignId}/batch-settings`, { pause_batch: selectedBatch }).then(() => fetchData());
                          }} data-testid="pause-batch-btn">
                          Pause Batch
                        </Button>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}

          {/* Batch Edit Panel */}
          {showBatchEdit && (
            <div className="p-4 border-t border-gray-100 space-y-3">
              <p className="text-xs font-medium text-gray-700">Time Slots (Malaysia Time)</p>
              {editSlots.map((slot, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <span className="text-xs text-gray-500 w-14">Slot {idx + 1}</span>
                  <input
                    type="time"
                    value={`${String(slot.start_hour).padStart(2, '0')}:${String(slot.start_minute).padStart(2, '0')}`}
                    onChange={(e) => {
                      const [h, m] = e.target.value.split(':').map(Number);
                      const updated = [...editSlots];
                      updated[idx] = { start_hour: h, start_minute: m };
                      setEditSlots(updated);
                    }}
                    className="border border-gray-200 rounded px-2 py-1 text-sm w-36 bg-white"
                    data-testid={`edit-slot-${idx}`}
                  />
                  {editSlots.length > 1 && (
                    <button onClick={() => setEditSlots(editSlots.filter((_, i) => i !== idx))} className="text-red-500 hover:text-red-700 text-sm">✕</button>
                  )}
                </div>
              ))}
              <button onClick={() => setEditSlots([...editSlots, { start_hour: 14, start_minute: 0 }])} className="text-xs text-indigo-600 hover:text-indigo-800 underline">+ Add Slot</button>
              <div className="flex gap-2 pt-2">
                <Button size="sm" onClick={saveBatchSlots} disabled={batchSaving} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white" data-testid="save-batch-slots-btn">
                  {batchSaving ? 'Saving...' : 'Save Changes'}
                </Button>
                <Button variant="outline" size="sm" onClick={() => { setShowBatchEdit(false); setEditSlots(campaign.batch_time_slots || []); }}>Cancel</Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Queue Table - Show for all campaigns except draft */}
      {campaign?.status !== 'draft' && (
        <Card className="bg-white border border-gray-100">
          {/* Filters */}
          <div className="p-4 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              {(() => {
                // Use batch-specific stats when a batch is selected
                const batchData = selectedBatch !== null && selectedBatch !== 'pending' && batches.find(b => b.batch_number === selectedBatch);
                const s = batchData ? batchData.stats : (campaign?.stats || {});
                const allCount = (s.total || 0) + (s.scheduled || 0);
                return (
                  <>
              <button
                onClick={() => { setFilter('all'); setPage(1); if (selectedBatch === 'pending') setSelectedBatch(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-[#4F46E5] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="filter-all"
              >
                All ({allCount})
              </button>
              <button
                onClick={() => { setFilter('pending'); setPage(1); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === 'pending' 
                    ? 'bg-yellow-500 text-white' 
                    : 'bg-yellow-50 text-yellow-700 hover:bg-yellow-100'
                }`}
                data-testid="filter-pending"
              >
                Pending ({s.pending || 0})
              </button>
              <button
                onClick={() => { setFilter('sent'); setPage(1); if (selectedBatch === 'pending') setSelectedBatch(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === 'sent' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-green-50 text-green-700 hover:bg-green-100'
                }`}
                data-testid="filter-sent"
              >
                Sent ({s.sent || 0})
              </button>
              <button
                onClick={() => { setFilter('failed'); setPage(1); if (selectedBatch === 'pending') setSelectedBatch(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === 'failed' 
                    ? 'bg-red-500 text-white' 
                    : 'bg-red-50 text-red-700 hover:bg-red-100'
                }`}
                data-testid="filter-failed"
              >
                Failed ({s.failed || 0})
              </button>
              <button
                onClick={() => { setFilter('skipped'); setPage(1); if (selectedBatch === 'pending') setSelectedBatch(null); }}
                className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                  filter === 'skipped' 
                    ? 'bg-gray-500 text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
                data-testid="filter-skipped"
              >
                Skipped ({s.skipped || 0})
              </button>
                  </>
                );
              })()}
            </div>
            <div className="flex items-center gap-2">
              <div className="relative" data-testid="export-dropdown-wrapper">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowExportMenu(!showExportMenu)}
                  disabled={(campaign?.stats?.sent || 0) === 0}
                  data-testid="export-queue"
                >
                  <Download size={14} className="mr-2" />
                  Export
                </Button>
                {showExportMenu && (
                  <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-20 py-1 min-w-[180px]">
                    <button
                      className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-gray-700"
                      onClick={async () => { setShowExportMenu(false); await doExport(null); }}
                    >All Batches</button>
                    {batches.length > 0 && batches.map(b => (
                      <button
                        key={b.batch_number}
                        className="w-full text-left px-4 py-2 text-sm hover:bg-gray-50 text-gray-700"
                        onClick={async () => { setShowExportMenu(false); await doExport(b.batch_number); }}
                      >Batch {b.batch_number + 1} ({b.stats.sent} sent)</button>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                variant="outline" 
                size="sm"
                onClick={fetchData}
                data-testid="refresh-queue"
              >
                <RefreshCw size={14} className="mr-2" />
                Refresh
              </Button>
            </div>
          </div>

          {/* Table */}
          <div className="overflow-x-auto" data-testid="send-queue-table">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">#</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Message</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {/* Show queue items if available */}
                {queue.length > 0 ? queue.map((message, index) => {
                  const statusInfo = getStatusBadge(message.status);
                  const rowNumber = (page - 1) * 10 + index + 1;
                  
                  return (
                    <tr 
                      key={message.id} 
                      className="hover:bg-gray-50"
                      data-testid={`queue-row-${message.id}`}
                    >
                      <td className="px-4 py-4 text-gray-500">{rowNumber}</td>
                      <td className="px-4 py-4">
                        <p 
                          className="font-medium text-gray-900"
                          data-testid={`queue-name-${message.id}`}
                        >
                          {message.client_name}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <p 
                          className="text-gray-600"
                          data-testid={`queue-phone-${message.id}`}
                        >
                          {message.client_phone}
                        </p>
                      </td>
                      <td className="px-4 py-4">
                        <span 
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}
                          data-testid={`queue-status-${message.id}`}
                        >
                          {statusInfo.icon}
                          {message.status}
                        </span>
                        {message.error_note && (
                          <p className="text-xs text-red-500 mt-1">{message.error_note}</p>
                        )}
                        {message.skip_reason && (
                          <p className="text-xs text-amber-600 mt-1">{message.skip_reason}</p>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => openPreview(message)}
                          className="text-gray-500"
                        >
                          <Eye size={14} className="mr-1" />
                          Preview
                        </Button>
                      </td>
                      <td className="px-4 py-4">
                        <TooltipProvider delayDuration={200}>
                          <div className="flex items-center gap-2">
                            {message.status === 'pending' && (
                              <>
                                {/* View Image button - show only if message has media */}
                                {message.media_url && (
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <Button 
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleCopyImage(message.media_url, message.id)}
                                        className={copiedImageId === message.id ? 'text-green-600 border-green-200 bg-green-50' : 'text-blue-600 border-blue-200 hover:bg-blue-50'}
                                      >
                                        {copiedImageId === message.id ? (
                                          <><Check size={14} className="mr-1" /> Copied</>
                                        ) : (
                                          <><Copy size={14} className="mr-1" /> Copy Image</>
                                        )}
                                      </Button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>Copy image to clipboard — paste directly in WhatsApp chat</p>
                                    </TooltipContent>
                                  </Tooltip>
                                )}
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      size="sm"
                                      onClick={() => handleOpenWhatsApp(message)}
                                      className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
                                      data-testid={`wa-send-btn-${message.id}`}
                                    >
                                      <MessageSquare size={14} className="mr-1" />
                                      Open WhatsApp
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Open WhatsApp with pre-filled message</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleMarkSent(message.id)}
                                      disabled={actionLoading[message.id] === 'sent'}
                                      className="text-green-600 border-green-200 hover:bg-green-50"
                                      data-testid={`mark-sent-btn-${message.id}`}
                                    >
                                      {actionLoading[message.id] === 'sent' ? (
                                        <Loader2 className="animate-spin" size={14} />
                                      ) : (
                                        <CheckCircle size={14} />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Mark as Sent</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => { setReasonModal({ messageId: message.id, action: 'skip' }); setReasonText(''); }}
                                      disabled={actionLoading[message.id] === 'skip'}
                                      className="text-gray-500"
                                      data-testid={`skip-btn-${message.id}`}
                                    >
                                      {actionLoading[message.id] === 'skip' ? (
                                        <Loader2 className="animate-spin" size={14} />
                                      ) : (
                                        <SkipForward size={14} />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Skip this message</p>
                                  </TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => { setReasonModal({ messageId: message.id, action: 'failed' }); setReasonText(''); }}
                                      disabled={actionLoading[message.id] === 'failed'}
                                      className="text-red-600 border-red-200 hover:bg-red-50"
                                      data-testid={`mark-failed-btn-${message.id}`}
                                    >
                                      {actionLoading[message.id] === 'failed' ? (
                                        <Loader2 className="animate-spin" size={14} />
                                      ) : (
                                        <XCircle size={14} />
                                      )}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Mark as Failed</p>
                                  </TooltipContent>
                                </Tooltip>
                              </>
                            )}
                            {message.status === 'failed' && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button 
                                    variant="outline" 
                                    size="sm"
                                    onClick={() => handleRetry(message.id)}
                                    disabled={actionLoading[message.id] === 'retry'}
                                    className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                  >
                                    {actionLoading[message.id] === 'retry' ? (
                                      <Loader2 className="animate-spin mr-1" size={14} />
                                    ) : (
                                      <RotateCcw size={14} className="mr-1" />
                                    )}
                                    Retry
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>Reset to pending and try again</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            {message.status === 'skipped' && (
                              <div className="flex items-center gap-2">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button 
                                      variant="outline" 
                                      size="sm"
                                      onClick={() => handleRetry(message.id)}
                                      disabled={actionLoading[message.id] === 'retry'}
                                      className="text-blue-600 border-blue-200 hover:bg-blue-50"
                                      data-testid={`retry-skipped-btn-${message.id}`}
                                    >
                                      {actionLoading[message.id] === 'retry' ? (
                                        <Loader2 className="animate-spin mr-1" size={14} />
                                      ) : (
                                        <RotateCcw size={14} className="mr-1" />
                                      )}
                                      Retry
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>
                                    <p>Reset to pending to retry sending</p>
                                  </TooltipContent>
                                </Tooltip>
                              </div>
                            )}
                            {message.status === 'sent' && (
                              <span className="text-sm text-gray-400">
                                Sent {message.sent_at ? formatDateTimeMY(message.sent_at) : ''}
                              </span>
                            )}
                          </div>
                        </TooltipProvider>
                      </td>
                    </tr>
                  );
                }) : campaign?.status === 'scheduled' && recipients.length > 0 ? (
                  // For scheduled campaigns, show recipients from group
                  recipients.map((recipient, index) => (
                    <tr key={recipient.client_id || index} className="hover:bg-gray-50">
                      <td className="px-4 py-4 text-gray-500">{index + 1}</td>
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-900">{recipient.client_name}</p>
                      </td>
                      <td className="px-4 py-4">
                        <p className="text-gray-600">{recipient.client_phone}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-700">
                          <Clock size={12} />
                          Scheduled
                        </span>
                      </td>
                      <td className="px-4 py-4 text-gray-400 text-sm">
                        Will send when campaign starts
                      </td>
                      <td className="px-4 py-4 text-gray-400 text-sm">
                        -
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="px-4 py-12 text-center">
                      <MessageSquare className="mx-auto text-gray-300 mb-3" size={40} />
                      <p className="text-gray-500 mb-1">
                        {filter !== 'all' 
                          ? `No ${filter} messages` 
                          : 'No messages in queue'}
                      </p>
                      {filter === 'all' && campaign?.stats?.total === 0 && (
                        <>
                          <p className="text-xs text-gray-400 mb-4">
                            The campaign may not have started or has no recipients yet
                          </p>
                          <div className="flex justify-center gap-3">
                            <Button 
                              variant="outline"
                              onClick={openAddGroupModal}
                              className="border-purple-200 text-purple-600 hover:bg-purple-50"
                            >
                              <UsersRound size={16} className="mr-2" />
                              Add Client Group
                            </Button>
                            <Button 
                              onClick={openAddClientsModal}
                              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                            >
                              <UserPlus size={16} className="mr-2" />
                              Add Clients
                            </Button>
                          </div>
                        </>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="p-4 border-t border-gray-100 flex items-center justify-between">
              <p className="text-sm text-gray-500">
                Page {page} of {totalPages} ({total} total)
              </p>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card>
      )}

      {/* Preview Modal */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Message Preview</DialogTitle>
          </DialogHeader>
          
          {previewMessage && (
            <div className="space-y-4">
              <div className="flex items-center justify-between text-sm">
                <span className="text-gray-500">To:</span>
                <span className="font-medium">{previewMessage.client_name} ({previewMessage.client_phone})</span>
              </div>
              
              <div className="bg-[#DCF8C6] rounded-lg p-4">
                {/* Image Preview */}
                {previewMessage.media_url && !previewMessage.media_url.includes('youtube') && !previewMessage.media_url.includes('youtu.be') && (
                  <div 
                    className="mb-3 cursor-pointer"
                    onClick={() => setImagePreviewUrl(previewMessage.media_url)}
                  >
                    <img 
                      src={previewMessage.media_url} 
                      alt="Media preview" 
                      className="max-h-40 rounded-lg shadow-sm hover:opacity-90 transition-opacity"
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                )}
                
                <p className="whitespace-pre-wrap text-sm">{previewMessage.message_content}</p>
                
                {/* Only show URL if template has show_media_url enabled */}
                {previewMessage.media_url && previewMessage.show_media_url && (
                  <p className="mt-2 text-blue-600 text-xs break-all">
                    {previewMessage.media_url}
                  </p>
                )}
              </div>
              
              <div className="flex justify-end gap-3">
                <Button 
                  variant="outline"
                  onClick={() => setShowPreview(false)}
                >
                  Close
                </Button>
                {previewMessage.status === 'pending' && (
                  <Button 
                    onClick={() => {
                      handleOpenWhatsApp(previewMessage);
                      setShowPreview(false);
                    }}
                    className="bg-[#25D366] hover:bg-[#20bd5a] text-white"
                  >
                    <ExternalLink size={14} className="mr-2" />
                    Open WhatsApp
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full-size Image Preview Modal */}
      {imagePreviewUrl && (
        <div 
          className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4"
          onClick={() => setImagePreviewUrl(null)}
        >
          <button
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors"
            onClick={() => setImagePreviewUrl(null)}
          >
            <X size={32} />
          </button>
          <img 
            src={imagePreviewUrl} 
            alt="Full size preview" 
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Add Clients Modal */}
      <Dialog open={showAddClientsModal} onOpenChange={setShowAddClientsModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Clients to Campaign</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Search */}
            <div className="flex gap-2">
              <Input
                placeholder="Search by name, email, or phone..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && searchClients()}
              />
              <Button onClick={searchClients} disabled={loadingClients}>
                <Search size={16} className="mr-2" />
                Search
              </Button>
            </div>
            
            {/* Client List */}
            <div className="max-h-64 overflow-y-auto border rounded-lg">
              {loadingClients ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
                </div>
              ) : availableClients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  No clients found. Try searching.
                </div>
              ) : (
                <div className="divide-y">
                  {availableClients.map((client) => (
                    <label 
                      key={client.id}
                      className={`flex items-center gap-3 p-3 hover:bg-gray-50 cursor-pointer ${selectedClients.includes(client.id) ? 'bg-indigo-50' : ''}`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedClients.includes(client.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedClients([...selectedClients, client.id]);
                          } else {
                            setSelectedClients(selectedClients.filter(id => id !== client.id));
                          }
                        }}
                        className="rounded"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{client.name}</p>
                        <p className="text-sm text-gray-500">{client.phone}</p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
            
            <div className="flex justify-between items-center pt-4 border-t">
              <span className="text-sm text-gray-500">
                {selectedClients.length} selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowAddClientsModal(false)}>
                  Cancel
                </Button>
                <Button
                  onClick={handleAddClients}
                  disabled={selectedClients.length === 0 || addingClients}
                  className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                >
                  {addingClients ? (
                    <Loader2 className="animate-spin mr-2" size={16} />
                  ) : (
                    <UserPlus size={16} className="mr-2" />
                  )}
                  Add Clients
                </Button>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client Group Modal */}
      <Dialog open={showAddGroupModal} onOpenChange={setShowAddGroupModal}>
        <DialogContent className="max-w-lg max-h-[80vh]">
          <DialogHeader>
            <DialogTitle>Add Client Group to Campaign</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-sm text-gray-500">
              Select a group to add all its members to this campaign. Clients already in the queue will be skipped.
            </p>
            
            {loadingGroups ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="animate-spin text-[#4F46E5]" size={24} />
              </div>
            ) : groups.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <UsersRound className="mx-auto text-gray-300 mb-3" size={40} />
                <p>No groups found</p>
                <p className="text-xs mt-1">Create a group in WhatsApp Broadcast → Groups first</p>
              </div>
            ) : (
              <div className="max-h-72 overflow-y-auto border rounded-lg divide-y">
                {groups.map((group) => (
                  <label 
                    key={group.id}
                    className={`flex items-center gap-3 p-4 hover:bg-gray-50 cursor-pointer ${selectedGroup === group.id ? 'bg-purple-50' : ''}`}
                  >
                    <input
                      type="radio"
                      name="group"
                      checked={selectedGroup === group.id}
                      onChange={() => setSelectedGroup(group.id)}
                      className="text-purple-600"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-gray-900">{group.name}</p>
                      <p className="text-sm text-gray-500">
                        {group.member_count || group.members?.length || 0} members
                      </p>
                    </div>
                  </label>
                ))}
              </div>
            )}
            
            <div className="flex justify-end gap-2 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowAddGroupModal(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleAddGroup}
                disabled={!selectedGroup || addingGroup}
                className="bg-purple-600 hover:bg-purple-700 text-white"
              >
                {addingGroup ? (
                  <Loader2 className="animate-spin mr-2" size={16} />
                ) : (
                  <UsersRound size={16} className="mr-2" />
                )}
                Add Group
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Reason Modal */}
      <Dialog open={!!reasonModal} onOpenChange={(open) => { if (!open) setReasonModal(null); }}>
        <DialogContent className="sm:max-w-[425px]">
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {reasonModal?.action === 'skip' ? 'Skip Message' : 'Mark as Failed'} — Reason
            </h3>
            <div className="space-y-2">
              {SKIP_REASONS.map((r) => (
                <button
                  key={r}
                  onClick={() => setReasonText(r)}
                  className={`w-full text-left px-3 py-2 rounded-lg border text-sm transition-colors ${
                    reasonText === r ? 'border-[#4F46E5] bg-indigo-50 text-[#4F46E5]' : 'border-gray-200 hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  {r}
                </button>
              ))}
            </div>
            <Input
              placeholder="Or type a custom reason..."
              value={SKIP_REASONS.includes(reasonText) ? '' : reasonText}
              onChange={(e) => setReasonText(e.target.value)}
              data-testid="reason-input"
            />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" size="sm" onClick={() => setReasonModal(null)}>Cancel</Button>
              <Button
                size="sm"
                onClick={submitReasonAndAction}
                className={reasonModal?.action === 'skip' ? 'bg-amber-500 hover:bg-amber-600 text-white' : 'bg-red-500 hover:bg-red-600 text-white'}
                data-testid="submit-reason-btn"
              >
                {reasonModal?.action === 'skip' ? 'Skip' : 'Mark Failed'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Debug Scheduler Modal */}
      <Dialog open={showDebug} onOpenChange={setShowDebug}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Scheduler Debug Info</DialogTitle>
          </DialogHeader>
          {debugInfo && (
            <div className="space-y-3 text-sm max-h-[60vh] overflow-y-auto">
              <div className="p-3 bg-gray-50 rounded-lg space-y-1">
                <p><strong>Campaign:</strong> {debugInfo.campaign_name}</p>
                <p><strong>Status:</strong> {debugInfo.status} | <strong>Type:</strong> {debugInfo.type}</p>
                <p><strong>Batch Mode:</strong> {debugInfo.batch_mode ? 'Yes' : 'No'}</p>
                <p><strong>Batch Index:</strong> {debugInfo.batch_current_index} / {debugInfo.total_distinct_batches} batches</p>
              </div>
              <div className="p-3 bg-blue-50 rounded-lg space-y-1">
                <p><strong>Now (MYT):</strong> {debugInfo.now_myt}</p>
                <p><strong>Scheduled At (MYT):</strong> {debugInfo.scheduled_at_myt || 'None'}</p>
                <p><strong>Is Due:</strong> {debugInfo.is_due ? '✅ Yes' : '❌ No'}</p>
                <p><strong>Would Scheduler Find It:</strong> {debugInfo.would_scheduler_find_it ? '✅ Yes' : '❌ No'}</p>
              </div>
              <div className="p-3 bg-yellow-50 rounded-lg space-y-1">
                <p><strong>Is Blackout Today:</strong> {debugInfo.is_blackout_today ? '⛔ Yes' : '✅ No'}</p>
                <p><strong>Send Days:</strong> {debugInfo.send_days_config ? debugInfo.send_days_config.join(', ') : 'Not configured (all days allowed)'}</p>
                <p><strong>Current Weekday:</strong> {debugInfo.current_weekday} ({['Mon','Tue','Wed','Thu','Fri','Sat','Sun'][debugInfo.current_weekday]})</p>
                <p><strong>Is Valid Send Day:</strong> {debugInfo.is_valid_send_day ? '✅ Yes' : '❌ No'}</p>
              </div>
              <div className="p-3 bg-gray-50 rounded-lg space-y-1">
                <p><strong>Queue Items (batch {debugInfo.batch_current_index}, scheduled):</strong> {debugInfo.queue_items_for_current_batch_scheduled}</p>
                <p><strong>Queue Items (total):</strong> {debugInfo.queue_items_total}</p>
                <p><strong>Time Slots:</strong> {JSON.stringify(debugInfo.batch_time_slots)}</p>
                <p><strong>Paused Batches:</strong> {JSON.stringify(debugInfo.paused_batches)}</p>
              </div>
              {debugInfo.blockers && (
                <div className={`p-3 rounded-lg space-y-1 ${debugInfo.blockers[0]?.includes('No blockers') ? 'bg-green-50' : 'bg-red-50'}`}>
                  <p className="font-semibold">{debugInfo.blockers[0]?.includes('No blockers') ? '✅ No Blockers' : '⚠️ Blockers Found:'}</p>
                  <ul className="list-disc list-inside text-xs">
                    {debugInfo.blockers.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WASendQueuePage;
